import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Sparkles, Calendar, TrendingUp, DollarSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format, differenceInDays } from "date-fns";

export function CostTransitionDashboard() {
  const { data: firstCustomerMetric } = useQuery({
    queryKey: ['first-customer-metric'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_metrics')
        .select('*')
        .eq('metric_name', 'first_customer_date')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  const { data: totalCustomers } = useQuery({
    queryKey: ['total-customers'],
    queryFn: async () => {
      const { count } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');
      
      return count || 0;
    }
  });

  const { data: recentAnalytics } = useQuery({
    queryKey: ['recent-analytics'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cost_analytics_daily')
        .select('*')
        .eq('is_baseline', false)
        .order('date', { ascending: false })
        .limit(30);
      
      return data || [];
    }
  });

  const hasFirstCustomer = !!firstCustomerMetric;
  const projectStartDate = new Date('2025-01-01'); // This should be dynamic
  const daysSinceStart = differenceInDays(new Date(), projectStartDate);

  if (!hasFirstCustomer) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">Zero Cost Mode</h3>
            <p className="text-muted-foreground mb-4">
              Platform is operational with no active customers. Waiting for your first subscriber!
            </p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-2xl font-bold text-primary">$0</div>
                <div className="text-sm text-muted-foreground">Current Monthly Cost</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{daysSinceStart}</div>
                <div className="text-sm text-muted-foreground">Days Operational</div>
              </div>
            </div>
            <Badge variant="outline" className="bg-background">
              <Calendar className="h-3 w-3 mr-1" />
              Started {format(projectStartDate, 'MMM d, yyyy')}
            </Badge>
          </div>
        </div>
      </Card>
    );
  }

  const firstCustomerDate = new Date(firstCustomerMetric.metric_date);
  const daysToFirstCustomer = differenceInDays(firstCustomerDate, projectStartDate);
  const daysSinceFirstCustomer = differenceInDays(new Date(), firstCustomerDate);
  
  const totalRevenue = recentAnalytics.reduce((sum, day) => sum + (day.revenue || 0), 0);
  const totalProfit = recentAnalytics.reduce((sum, day) => sum + (day.profit || 0), 0);

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary text-primary-foreground">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              First Customer Milestone! 🎉
            </h3>
            <p className="text-muted-foreground mb-4">
              Your journey from $0 to active operations began on {format(firstCustomerDate, 'MMMM d, yyyy')}
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-2xl font-bold text-primary">{daysToFirstCustomer}</div>
                <div className="text-sm text-muted-foreground">Days to First Customer</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{daysSinceFirstCustomer}</div>
                <div className="text-sm text-muted-foreground">Days Since First</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totalCustomers}</div>
                <div className="text-sm text-muted-foreground">Active Customers</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Growth Metrics</h4>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <span className="font-semibold">${totalRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Profit</span>
              <span className="font-semibold text-primary">${totalProfit.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Daily Revenue</span>
              <span className="font-semibold">
                ${recentAnalytics.length > 0 ? (totalRevenue / recentAnalytics.length).toFixed(2) : '0.00'}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Platform Timeline</h4>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2" />
              <div className="flex-1">
                <div className="font-medium text-sm">Project Launch</div>
                <div className="text-xs text-muted-foreground">
                  {format(projectStartDate, 'MMM d, yyyy')}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <div className="flex-1">
                <div className="font-medium text-sm">First Customer</div>
                <div className="text-xs text-muted-foreground">
                  {format(firstCustomerDate, 'MMM d, yyyy')} (+{daysToFirstCustomer} days)
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2" />
              <div className="flex-1">
                <div className="font-medium text-sm">Today</div>
                <div className="text-xs text-muted-foreground">
                  {totalCustomers} active customer{totalCustomers !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
